import React, { useCallback, useEffect, useState } from "react";
import { ArrowPathIcon, PhotoIcon, CheckIcon, XMarkIcon, LinkIcon, CalendarDaysIcon } from "@heroicons/react/24/outline";
import { approveBanner, deactivateBanner, getActiveBanners, getPendingBanners, rejectBanner } from "../api/paidBannersAPI";

const errText = (e, fallback) => {
  if (e?.status === 401) return "Сессия истекла. Войдите в админ-панель заново.";
  if (e?.status === 403) return "Недостаточно прав для модерации баннеров.";
  if (e?.status === 404) return "Endpoint не найден. Backend не задеплоен на этот сервер.";
  if (e?.status >= 500) return "Ошибка сервера. Повторите позже.";
  return e?.message || fallback;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const daysLeft = (iso) => {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  return d > 0 ? `${d} дн` : "истекает";
};

const btn = "rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50";

function SlotCard({ slot, mode, onDone }) {
  const [panel, setPanel] = useState(null); // 'reject' | 'deactivate'
  const [reason, setReason] = useState("");
  const [refund, setRefund] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const run = async (fn, message) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onDone(message);
    } catch (e) {
      setErr(errText(e, "Не удалось выполнить действие"));
      setBusy(false);
    }
  };

  const left = mode === "active" ? daysLeft(slot.expires_at) : null;

  return (
    <article className={`flex flex-col overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm ${busy ? "opacity-60" : ""}`}>
      <div className="relative aspect-[3/1] bg-bg-tertiary">
        {slot.image_url ? (
          <a href={slot.image_url} target="_blank" rel="noopener noreferrer">
            <img src={slot.image_url} alt={slot.title || ""} className="h-full w-full object-cover" />
          </a>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-text-muted">
            <PhotoIcon className="h-8 w-8" />
          </div>
        )}
        <span className={`absolute left-2 top-2 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white shadow ${mode === "pending" ? "bg-amber-500" : "bg-emerald-600"}`}>
          {mode === "pending" ? "На проверке" : left ? `Активен · ${left}` : "Активен"}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div>
          <p className="truncate text-sm font-semibold text-text-primary">{slot.title || "Без названия"}</p>
          <p className="mt-0.5 text-xs text-text-muted">
            слот <span className="font-mono">#{slot.id}</span> · пользователь <span className="font-mono">#{slot.user_id ?? "—"}</span>
            {slot.announcement_id && (
              <>
                {" "}
                · объявление <span className="font-mono">#{slot.announcement_id}</span>
              </>
            )}
          </p>
        </div>

        <div className="space-y-1 text-xs text-text-secondary">
          <p className="flex items-center gap-1.5">
            <CalendarDaysIcon className="h-3.5 w-3.5 shrink-0 text-text-muted" />
            {slot.starts_at ? fmtDate(slot.starts_at) : "с момента одобрения"} → {fmtDate(slot.expires_at)}
          </p>
          {slot.cta_url && (
            <a href={slot.cta_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 truncate text-[#7C3AED] hover:underline dark:text-indigo-300">
              <LinkIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{slot.cta_url}</span>
            </a>
          )}
          <p className="text-text-muted">Заявка от {fmtDate(slot.created_at)}</p>
        </div>

        {err && <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-1.5 text-xs text-rose-500">{err}</p>}

        <div className="mt-auto">
          {panel ? (
            <div className="space-y-2">
              <textarea
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Причина — увидит продавец (необязательно)"
                rows={2}
                className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
              />
              {panel === "reject" ? (
                <p className="text-xs text-text-muted">Стоимость баннера вернётся на баланс продавца.</p>
              ) : (
                <label className="flex cursor-pointer items-center gap-2 text-xs text-text-secondary">
                  <input type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} className="h-3.5 w-3.5 accent-rose-600" />
                  Вернуть стоимость на баланс продавца
                </label>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    panel === "reject"
                      ? run(() => rejectBanner(slot.id, reason), `Слот #${slot.id} отклонён, средства возвращены продавцу.`)
                      : run(
                          () => deactivateBanner(slot.id, { reason, refund }),
                          `Слот #${slot.id} отключён${refund ? ", средства возвращены" : ""}.`
                        )
                  }
                  className={`${btn} inline-flex flex-1 items-center justify-center gap-1.5 bg-rose-600 text-white hover:bg-rose-700`}
                >
                  {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                  {panel === "reject" ? "Отклонить" : "Отключить"}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setPanel(null);
                    setReason("");
                    setRefund(false);
                  }}
                  className={`${btn} border border-border-primary text-text-secondary hover:bg-bg-tertiary`}
                >
                  Отмена
                </button>
              </div>
            </div>
          ) : mode === "pending" ? (
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => approveBanner(slot.id), `Слот #${slot.id} одобрен и показывается на главной.`)}
                className={`${btn} inline-flex flex-1 items-center justify-center gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700`}
              >
                {busy ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                Одобрить
              </button>
              <button type="button" disabled={busy} onClick={() => setPanel("reject")} className={`${btn} flex-1 border border-rose-500/40 text-rose-500 hover:bg-rose-500/10`}>
                Отклонить
              </button>
            </div>
          ) : (
            <button type="button" disabled={busy} onClick={() => setPanel("deactivate")} className={`${btn} w-full border border-border-primary text-text-secondary hover:border-rose-500/40 hover:text-rose-500`}>
              Отключить досрочно
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

const Group = ({ title, count, loading, empty, children }) => (
  <section>
    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-primary">
      {title}
      <span className="rounded-full bg-bg-tertiary px-2 text-xs tabular-nums text-text-muted">{loading ? "…" : count}</span>
    </h3>
    {loading && count === 0 ? (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="aspect-[3/2] animate-pulse rounded-block border border-border-primary bg-bg-card" />
        ))}
      </div>
    ) : count === 0 ? (
      <p className="rounded-block border border-dashed border-border-primary bg-bg-card px-6 py-8 text-center text-sm text-text-secondary">{empty}</p>
    ) : (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">{children}</div>
    )}
  </section>
);

function PaidBannersManagement() {
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    let firstError = null;
    const safe = (p, msg) =>
      p.catch((e) => {
        firstError = firstError || errText(e, msg);
        return [];
      });
    const [p, a] = await Promise.all([
      safe(getPendingBanners(), "Не удалось загрузить заявки"),
      safe(getActiveBanners(), "Не удалось загрузить активные баннеры"),
    ]);
    setPending(p);
    setActive(a);
    if (firstError) setToast({ type: "error", text: firstError });
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const done = (message) => {
    setToast({ type: "success", text: message });
    fetchData();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-text-secondary">Продавцы оплачивают показ своего баннера на главной рядом с редакционным.</p>
        <button type="button" onClick={fetchData} disabled={loading} title="Обновить" aria-label="Обновить" className="shrink-0 rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary">
          <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {toast && (
        <div
          role={toast.type === "error" ? "alert" : "status"}
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"
          }`}
        >
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <Group title="На модерации" count={pending.length} loading={loading} empty="Новых заявок нет 🎉">
        {pending.map((s) => (
          <SlotCard key={s.id} slot={s} mode="pending" onDone={done} />
        ))}
      </Group>

      <Group title="Активные" count={active.length} loading={loading} empty="Сейчас нет активных платных баннеров.">
        {active.map((s) => (
          <SlotCard key={s.id} slot={s} mode="active" onDone={done} />
        ))}
      </Group>
    </div>
  );
}

export default PaidBannersManagement;
